/* oxlint-disable */
'use client';

import React, { useEffect, useState, useRef } from 'react';

import Image from 'next/image';

import { Skeleton } from '@/components/ui/skeleton';

interface LazyFooterImageProps {
  src: string | any; // Can be import or string
  alt: string;
  width: number;
  height: number;
  className: string;
  priority: boolean;
}

// Custom hook for intersection observer
const useIntersectionObserver = (threshold = 0.1) => {
  const [isIntersecting, setIsIntersecting] = useState(false);
  const [hasIntersected, setHasIntersected] = useState(false);
  const elementRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    // Fallback for browsers without IntersectionObserver support
    if (!window.IntersectionObserver) {
      setIsIntersecting(true);
      setHasIntersected(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        const isVisible = entry.isIntersecting;
        setIsIntersecting(isVisible);

        // Once intersected, keep it loaded
        if (isVisible && !hasIntersected) {
          setHasIntersected(true);
        }
      },
      {
        root: null,
        threshold,
        rootMargin: '100px', // Load images 100px before footer enters viewport
      },
    );

    observer.observe(element);
    return () => { return observer.disconnect(); };
  }, [threshold, hasIntersected]);

  return { elementRef, isIntersecting: isIntersecting || hasIntersected };
};

const LazyFooterImage: React.FC<LazyFooterImageProps> = ({
  src,
  alt,
  width = 100,
  height = 50,
  className = '',
  priority = false,
}) => {
  const { elementRef, isIntersecting } = useIntersectionObserver();
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  // If priority is true, load immediately
  const shouldLoadImage = priority || isIntersecting;

  return (
    <div ref={elementRef} className="relative inline-block">
      {shouldLoadImage ? (
        <>
          {/* Loading skeleton */}
          {!imageLoaded && !imageError && (
            <Skeleton
              className="absolute inset-0 z-10 rounded"
              style={{ width: width || 'auto', height: height || 'auto' }}
            />
          )}

          {/* Error fallback */}
          {imageError && (
            <div
              className="bg-gray-700 rounded flex items-center justify-center text-gray-400 text-xs"
              style={{ width: width || 100, height: height || 50 }}
            >
              {alt}
            </div>
          )}

          <Image
            src={src}
            alt={alt}
            width={width}
            height={height}
            className={className}
            loading={priority ? 'eager' : 'lazy'}
            onLoad={() => {
              setImageLoaded(true);
              setImageError(false);
            }}
            onError={() => {
              setImageError(true);
              setImageLoaded(false);
            }}
            style={{
              opacity: imageLoaded ? 1 : 0,
              transition: 'opacity 0.3s ease-in-out',
            }}
          />
        </>
      ) : (
        // Placeholder skeleton for non-intersecting images
        <Skeleton
          className="rounded"
          style={{ width: width || 100, height: height || 50 }}
        />
      )}
    </div>
  );
};

export default LazyFooterImage;
